/**
 * Extended file type support for attachments.
 *
 * This module provides structured configuration for supported file types with:
 * - MIME type allowlist
 * - Per-type size limits
 * - Magic byte validation for security
 *
 * @see the extended-attachment-types deliberation in SchoolAI/cadence-reference
 * (was docs/deliberations/extended-attachment-types/, moved in #11745)
 */

// ============================================================================
// Types
// ============================================================================

export interface MagicByteSignature {
  /** Byte offset from start of file */
  offset: number
  /** Expected bytes at that offset */
  bytes: number[]
}

export interface FileTypeConfig {
  /** Category for grouping (images, documents, spreadsheets) */
  category: string
  /** Allowed MIME types for this category */
  mimeTypes: readonly string[]
  /** Allowed file extensions (with leading dot) */
  extensions: readonly string[]
  /** Maximum file size in bytes */
  maxSizeBytes: number
  /**
   * Magic byte signatures for content validation.
   *
   * Note: some MIME types within a category bypass magic byte validation even
   * when this array is non-empty. Specifically, `isVideoContainerType()` skips
   * validation for MP4/WebM/MOV (container formats whose magic byte offsets
   * vary by encoding), mirroring how `isTextBasedType()` skips text/* types.
   * If you add new MIME types to a category, check both helpers to understand
   * whether they bypass this array.
   */
  magicBytes: readonly MagicByteSignature[]
}

export interface ValidationResult {
  valid: boolean
  error?: string
  config?: FileTypeConfig
}

// ============================================================================
// Configuration
// ============================================================================

/**
 * Supported file types with their configurations.
 *
 * Categories:
 * - images: JPEG, PNG, WebP (10MB limit)
 * - documents: PDF, DOCX, PPTX, TXT, Markdown (25MB limit)
 * - spreadsheets: XLSX, CSV (25MB limit)
 * - videos: GIF, MP4, WebM, MOV/QuickTime (50MB limit)
 */
export const SUPPORTED_FILE_TYPES: readonly FileTypeConfig[] = [
  {
    category: 'images',
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    extensions: ['.jpg', '.jpeg', '.png', '.webp'],
    maxSizeBytes: 10 * 1024 * 1024, // 10MB
    magicBytes: [
      // JPEG: FFD8FF
      { offset: 0, bytes: [0xff, 0xd8, 0xff] },
      // PNG: 89504E47 (first 4 bytes of 8-byte signature)
      { offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] },
      // WebP: RIFF....WEBP (bytes 8-11 are "WEBP")
      { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] },
    ],
  },
  {
    category: 'videos',
    mimeTypes: ['image/gif', 'video/mp4', 'video/webm', 'video/quicktime'],
    extensions: ['.gif', '.mp4', '.webm', '.mov'],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    magicBytes: [
      // GIF: "GIF" ASCII (47 49 46) at offset 0
      { offset: 0, bytes: [0x47, 0x49, 0x46] },
      // MP4/WebM/MOV: magic byte validation is intentionally skipped for
      // video container formats. MP4, WebM, and QuickTime containers have
      // encoding-dependent byte patterns (ftyp brand at offset 4 for MP4,
      // EBML header for WebM, but offsets vary by muxer). False positives and
      // false negatives make byte-level validation unreliable. The MIME type
      // allowlist and extension allowlist together provide sufficient
      // protection for these types — the upload path also enforces size
      // limits and org-scoped storage, making content spoofing impractical.
    ],
  },
  {
    category: 'documents',
    mimeTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/markdown',
    ],
    extensions: ['.pdf', '.docx', '.pptx', '.txt', '.md'],
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    magicBytes: [
      // PDF: %PDF (25504446)
      { offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] },
      // DOCX/PPTX (ZIP format): PK.. (504B0304)
      { offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] },
      // text/plain and text/markdown have no magic bytes - handled separately
    ],
  },
  {
    category: 'spreadsheets',
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ],
    extensions: ['.xlsx', '.csv'],
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    magicBytes: [
      // XLSX (ZIP format): PK.. (504B0304)
      { offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] },
      // CSV has no magic bytes - handled separately
    ],
  },
] as const

// ============================================================================
// Lookup Functions
// ============================================================================

/**
 * Find the file type configuration for a given MIME type.
 */
export function getFileTypeConfig(
  mimeType: string,
): FileTypeConfig | undefined {
  return SUPPORTED_FILE_TYPES.find(ft => ft.mimeTypes.includes(mimeType))
}

/**
 * Get the maximum allowed file size for a MIME type.
 * Returns 0 if the MIME type is not supported.
 */
export function getMaxSizeForType(mimeType: string): number {
  const config = getFileTypeConfig(mimeType)
  return config?.maxSizeBytes ?? 0
}

/**
 * Check if a MIME type is in the supported allowlist.
 */
export function isSupportedFileType(mimeType: string): boolean {
  return SUPPORTED_FILE_TYPES.some(ft => ft.mimeTypes.includes(mimeType))
}

/**
 * Get all supported MIME types as a flat array.
 */
export function getSupportedMimeTypes(): string[] {
  return SUPPORTED_FILE_TYPES.flatMap(ft => ft.mimeTypes)
}

/**
 * Get all supported file extensions for use in accept attribute.
 */
export function getSupportedExtensions(): string[] {
  return SUPPORTED_FILE_TYPES.flatMap(ft => ft.extensions)
}

/**
 * Get a human-readable description of supported file types.
 */
export function getSupportedTypesDescription(): string {
  const imageExts = ['JPEG', 'PNG', 'WebP']
  const docExts = ['PDF', 'Word', 'PowerPoint', 'Text', 'Markdown']
  const sheetExts = ['Excel', 'CSV']
  const videoExts = ['GIF', 'MP4', 'WebM', 'MOV']
  return `Images (${imageExts.join(', ')}), Documents (${docExts.join(', ')}), Spreadsheets (${sheetExts.join(', ')}), Videos (${videoExts.join(', ')})`
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Check if a MIME type is a text-based format without magic bytes.
 * These formats are validated by content parsing rather than magic bytes.
 */
function isTextBasedType(mimeType: string): boolean {
  return (
    mimeType === 'text/plain' ||
    mimeType === 'text/markdown' ||
    mimeType === 'text/csv'
  )
}

/**
 * Check if a MIME type is a video container format for which magic byte
 * validation is intentionally skipped.
 *
 * GIF uses `image/gif` and HAS a reliable 3-byte signature ("GIF" at offset 0)
 * so it is NOT included here. MP4, WebM, and QuickTime container magic bytes
 * vary by encoder/muxer, making byte-level validation unreliable — the MIME
 * type + extension allowlist is sufficient for these types.
 */
function isVideoContainerType(mimeType: string): boolean {
  return (
    mimeType === 'video/mp4' ||
    mimeType === 'video/webm' ||
    mimeType === 'video/quicktime'
  )
}

/**
 * Validate magic bytes against a file's content.
 */
function validateMagicBytes(
  fileBytes: Buffer | Uint8Array,
  signatures: readonly MagicByteSignature[],
): boolean {
  return signatures.some(({ offset, bytes }) => {
    if (fileBytes.length < offset + bytes.length) return false
    return bytes.every((byte, i) => fileBytes[offset + i] === byte)
  })
}

/**
 * Validate a file against the supported file types configuration.
 *
 * Checks:
 * 1. MIME type is in the allowlist
 * 2. File extension matches the MIME type's expected extensions
 * 3. Magic bytes match the file content (if bytes are provided and type has signatures)
 *
 * @param mimeType - The file's MIME type
 * @param fileName - The file name including extension
 * @param fileBytes - Optional: First ~16 bytes of the file for magic byte validation
 */
export function validateFileType(
  mimeType: string,
  fileName: string,
  fileBytes?: Buffer | Uint8Array,
): ValidationResult {
  // 1. Check MIME type is in allowlist
  const config = getFileTypeConfig(mimeType)
  if (!config) {
    const allExtensions = SUPPORTED_FILE_TYPES.flatMap(ft => ft.extensions)
    return {
      valid: false,
      error: `Unsupported file type. Supported: ${allExtensions.join(', ')}`,
    }
  }

  // 2. Validate extension matches config
  const ext = fileName.toLowerCase().match(/\.[^.]+$/)?.[0]
  if (!ext || !config.extensions.includes(ext)) {
    return {
      valid: false,
      error: `File extension does not match type. Expected: ${config.extensions.join(', ')}`,
    }
  }

  // 3. Validate magic bytes if provided
  if (fileBytes && fileBytes.length > 0) {
    // Text-based types don't have magic bytes - skip validation
    if (isTextBasedType(mimeType)) {
      return { valid: true, config }
    }

    // Video container formats (MP4, WebM, MOV) have encoding-dependent magic
    // bytes that make byte-level validation unreliable - skip validation.
    // GIF (image/gif) is NOT skipped: it has a reliable 3-byte signature.
    if (isVideoContainerType(mimeType)) {
      return { valid: true, config }
    }

    // If config has magic byte signatures, validate them
    if (config.magicBytes.length > 0) {
      const magicMatch = validateMagicBytes(fileBytes, config.magicBytes)
      if (!magicMatch) {
        return {
          valid: false,
          error: 'File content does not match declared type',
        }
      }
    }
  }

  return { valid: true, config }
}

/**
 * Validate file size against the per-type limit.
 *
 * @param mimeType - The file's MIME type
 * @param sizeBytes - The file size in bytes
 */
export function validateFileSize(
  mimeType: string,
  sizeBytes: number,
): ValidationResult {
  const config = getFileTypeConfig(mimeType)
  if (!config) {
    return {
      valid: false,
      error: 'Unsupported file type',
    }
  }

  if (sizeBytes <= 0) {
    return {
      valid: false,
      error: 'File size must be greater than 0',
    }
  }

  if (sizeBytes > config.maxSizeBytes) {
    const maxMB = config.maxSizeBytes / (1024 * 1024)
    return {
      valid: false,
      error: `File size must be less than ${maxMB}MB for ${config.category}`,
    }
  }

  return { valid: true, config }
}

// ============================================================================
// MIME Type Resolution (Issue #2951)
// ============================================================================

/**
 * Map of file extensions to their canonical MIME types.
 * Used as fallback when browsers report incorrect MIME types
 * (e.g., `application/octet-stream` or empty string for .md files).
 *
 * IMPORTANT: Keep in sync with SUPPORTED_FILE_TYPES above.
 * When adding a new file type, add its extension here too.
 */
const EXTENSION_TO_MIME: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv': 'text/csv',
  // Videos — keep in sync with the 'videos' category in SUPPORTED_FILE_TYPES
  '.gif': 'image/gif',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
}

/**
 * Resolve the correct MIME type for a file, falling back to extension-based
 * detection when the browser reports an incorrect or missing MIME type.
 *
 * Browsers often report `.md` files as `application/octet-stream` or empty
 * string instead of `text/markdown`. This function handles that case.
 *
 * @param fileName - The file name including extension
 * @param browserMimeType - The MIME type reported by the browser
 * @returns The resolved MIME type, or null if the file type is unsupported
 */
export function resolveFileMimeType(
  fileName: string,
  browserMimeType: string,
): string | null {
  // If browser reports a known supported MIME type, trust it
  if (browserMimeType && isSupportedFileType(browserMimeType)) {
    return browserMimeType
  }

  // Fall back to extension-based detection
  const ext = fileName.toLowerCase().match(/\.[^.]+$/)?.[0]
  if (ext) {
    const mimeFromExtension = EXTENSION_TO_MIME[ext]
    // Defense-in-depth: verify the mapped type is still in the allowlist
    if (mimeFromExtension && isSupportedFileType(mimeFromExtension)) {
      return mimeFromExtension
    }
  }

  // Truly unsupported
  return null
}

// ============================================================================
// Backward Compatibility
// ============================================================================

/**
 * @deprecated Use isSupportedFileType instead
 */
export function isSupportedImageType(mimeType: string): boolean {
  const imageConfig = SUPPORTED_FILE_TYPES.find(ft => ft.category === 'images')
  return imageConfig?.mimeTypes.includes(mimeType) ?? false
}

/**
 * @deprecated Use getMaxSizeForType instead
 */
export const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB (images default)
